/**
 * Shared helpers for the historical dashboards (INSIGHT-HIST-12 onwards).
 */

/**
 * Milliseconds as a duration people read at a glance: 793624 -> "13m 14s".
 *
 * Returns "No Data" for null or undefined. A real zero prints as "0s", because
 * missing data and a genuine zero must never look the same.
 */
export const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) {
    return "No Data";
  }

  const totalSeconds = Math.round(Number(ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
};

/** A percentage with one decimal, or "No Data" when it is unknown. */
export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "No Data";
  }

  return `${Number(value).toFixed(1)}%`;
};

/** A timestamp as local time, or "-" when there is none. */
export const formatTime = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

/** One option of a chip selector, as the portal's MultipleSelector uses them. */
export type SelectorOption = { id: string; label: string };

export type HistoricalFilterValues = {
  /** YYYY-MM-DD, the same shape the portal's date range picker produces. */
  startDate: string;
  endDate: string;
  /** react-date-range's own state, kept here so the calendar stays in sync. */
  dateSelectionRange: any;
  /** HH:mm, restaurant local time. Both or neither. */
  fromTime?: string;
  toTime?: string;
  modeList: SelectorOption[];
  platformList: SelectorOption[];
  channelList: SelectorOption[];
  stationId?: string;
  driverId?: string;
};

/**
 * The times a person can pick: every hour, plus 23:59 so a whole day can be
 * selected without the last hour falling outside an inclusive range.
 */
export const TIME_OPTIONS: string[] = [
  ...Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, "0")}:00`),
  "23:59",
];

const today = () => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * The filter state a page starts with: today, no chips, no time window.
 *
 * `dateSelectionRange` is react-date-range's own shape, the one the sales
 * filters use, so the calendar and the two dates cannot drift apart.
 */
export const defaultHistoricalFilters = (): HistoricalFilterValues => ({
  startDate: today(),
  endDate: today(),
  dateSelectionRange: [
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ],
  modeList: [],
  platformList: [],
  channelList: [],
});

/** The ids of a chip selection, as the comma list the API reads. */
const idsOf = (list: SelectorOption[] | undefined) =>
  (list || []).map((option) => option.id).join(",");

/** How many controls are actually narrowing the results, for the badge. */
export const countActiveFilters = (filters: HistoricalFilterValues): number => {
  let count = 0;

  if (filters.fromTime && filters.toTime) {
    count += 1;
  }

  [filters.modeList, filters.platformList, filters.channelList].forEach(
    (list) => {
      if (list && list.length) {
        count += 1;
      }
    },
  );

  if (filters.stationId) {
    count += 1;
  }

  if (filters.driverId) {
    count += 1;
  }

  return count;
};

/**
 * Turns the control values into the query the API expects.
 *
 * An empty chip list is left out rather than sent empty: choosing no mode
 * means every mode, not none.
 */
export const toQueryParams = (filters: HistoricalFilterValues) => {
  const params: Record<string, string> = {
    startDate: filters.startDate,
    endDate: filters.endDate,
  };

  // A half-set window would silently drop part of the day, so both or neither.
  if (filters.fromTime && filters.toTime) {
    params.fromTime = filters.fromTime;
    params.toTime = filters.toTime;
  }

  const mode = idsOf(filters.modeList);
  const platform = idsOf(filters.platformList);
  const channel = idsOf(filters.channelList);

  if (mode) {
    params.mode = mode;
  }

  if (platform) {
    params.platform = platform;
  }

  if (channel) {
    params.channel = channel;
  }

  if (filters.stationId) {
    params.stationId = filters.stationId;
  }

  if (filters.driverId) {
    params.driverId = filters.driverId;
  }

  return params;
};
